import clsx from 'clsx';
import React, { InputHTMLAttributes, useState } from 'react';
import { Corner, Shadow } from '../../utils/utils';
import { EyeIcon } from '@heroicons/react/24/outline';
import uiConfig from '../../data/uiConfig';

interface InputProps extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  centered?: boolean;
  ariaLabel?: string;
  label?: string;
  labelColor?: string;
  width?: number | string;
  corner?: Corner;
  shadow?: Shadow;
  bgColor?: string;
  color?: string;
  borderColor?: string;
  icon?: React.ReactElement;
  rows?: number;
  iconColor?: string;
  className?: string;
  error?: string;
}

export const Input: React.FC<InputProps> = ({
  type = "text",
  placeholder = "",
  ariaLabel = "Input field",
  required = false,
  centered = false,
  label,
  labelColor = uiConfig.input.labelColor,
  width = 20,
  corner = uiConfig.input.corner as Corner,
  shadow = uiConfig.input.shadow as Shadow,
  bgColor = uiConfig.input.bgColor,
  color = uiConfig.input.color,
  borderColor = uiConfig.input.borderColor,
  icon = null,
  rows,
  iconColor = uiConfig.input.iconColor,
  className = '',
  error,
  ...props
}) => {

  //Valeur par défaut
  placeholder = placeholder ? placeholder : type === 'email' ? 'Email' : type === 'password' ? 'Mot de passe' : type === 'number' ? 'Nombre' : 'Texte';
  ariaLabel = ariaLabel ? ariaLabel : label ? label : placeholder;

  // Ajout du state pour le type d'input
  const [inputType, setInputType] = useState(type);

  const handleInputType = () => {
    setInputType(prev =>
      prev === 'password' ? 'text' : 'password'
    );
  };

  return (
    <div className={clsx(centered && 'text-center')}>
        {label && (
          <label className={`block mb-1 font-thin ${labelColor}`}>
            {label} 
            {required &&
            <span className='text-alert ml-1'>*</span>
            }
          </label>
      )}
      <div
        className={clsx('relative', centered && 'inline-block')}
        style={{
          width: typeof width === 'number' ? `${width}rem` : width,
        }}
      >
        {rows ? (
          <textarea
            placeholder={placeholder}
            aria-label={ariaLabel}
            rows={rows}
            required={required}
            style={{
              resize: 'vertical',
            }}
            className={clsx(
              'px-3 py-2 outline-none transition-all w-full',
              bgColor,
              color,
              corner,
              shadow,
              borderColor && `border ${borderColor}`,
              className
            )}
            {...props}
          />
        ) : (
          <input
            type={inputType}
            placeholder={placeholder}
            aria-label={ariaLabel}
            required={required}
            className={clsx(
              'px-3 py-2 outline-none transition-all w-full',
              icon && 'pl-10',
              bgColor,
              color,
              corner,
              shadow,
              borderColor && `border ${borderColor}`,
              error && `!border-alert-400`,
              className
            )}
            {...(type === 'number' && { inputMode: 'numeric', pattern: '[0-9]*' })}
            {...(type === 'email' && { inputMode: 'email' })}
            {...(type === 'password' && { autoComplete: 'current-password' })}
            {...props}
          />
        )}
        {icon && (
          <span className="absolute left-3 top-3">
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { className: `h-5 w-5 ${color}` } as React.SVGProps<SVGSVGElement>)
              : icon}
          </span>
        )}
        {type === 'password' && (
          <span className="absolute right-3 top-3">
            <EyeIcon className={`h-5 w-5 ${iconColor} cursor-pointer`} onClick={() => handleInputType()} />
          </span>
        )}
        {error && (
          <span className="text-alert text-sm">{error}</span>
        )}
      </div>
    </div>
  )
};